import React, { useState, useEffect, useCallback } from "react";
import { User } from "@/contracts/auth-api.types";
import { userService } from "@/services/userService";
import { useNotification } from "@/hooks/useNotification";
import { Button } from "@/components/common/Button";
import { Badge } from "@/components/common/Badge";
import { PermissionGuard } from "@/components/guards/PermissionGuard";
import {
  Plus,
  Edit2,
  Trash2,
  Search,
  RefreshCw,
  ShieldAlert,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { formatDate } from "@/utils/formatters";
import { UserModal } from "./UserModal";

export const UsersList: React.FC = () => {
  const { notify } = useNotification();
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState<User | null>(null);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await userService.list(1, 100);
      const list = Array.isArray(response) ? response : response.data || [];
      setUsers(list);
    } catch (err: any) {
      notify(err.message || "Erro ao carregar lista de usuários", "error");
    } finally {
      setIsLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const filteredUsers = users.filter((u) => {
    const term = searchTerm.toLowerCase();
    return (
      u.name.toLowerCase().includes(term) ||
      u.email.toLowerCase().includes(term) ||
      (u.username && u.username.toLowerCase().includes(term))
    );
  });

  const handleDelete = async () => {
    if (!userToDelete) return;
    setIsDeleting(true);
    try {
      await userService.delete(userToDelete.id);
      notify(`Usuário "${userToDelete.name}" removido com sucesso`, "success");
      setUserToDelete(null);
      loadUsers();
    } catch (err: any) {
      notify(err.message || "Erro ao excluir o usuário", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestão de Usuários</h1>
          <p className="text-muted text-sm mt-0.5">
            Cadastre e gerencie identidades, perfis de acesso e credenciais dos usuários.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadUsers}
            isLoading={isLoading}
            title="Recarregar lista"
          >
            <RefreshCw size={16} />
            <span className="hide-sm">Recarregar</span>
          </Button>

          <PermissionGuard permission="users:create">
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                setUserToEdit(null);
                setIsModalOpen(true);
              }}
            >
              <Plus size={16} />
              <span>Novo Usuário</span>
            </Button>
          </PermissionGuard>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="card p-4">
        <div className="flex items-center gap-3">
          <div className="search-input-wrapper flex-1">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Buscar por nome, e-mail ou username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          {searchTerm && (
            <Button variant="ghost" size="sm" onClick={() => setSearchTerm("")}>
              Limpar
            </Button>
          )}
        </div>
      </div>

      {/* Users Table */}
      <div className="card p-0 overflow-hidden">
        {isLoading ? (
          <div className="py-12 text-center">
            <div className="spinner mx-auto mb-3"></div>
            <p className="text-sm text-muted">Carregando usuários...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="py-12 text-center text-muted">
            <p className="text-base font-medium">Nenhum usuário encontrado.</p>
            <p className="text-sm mt-1">Crie um novo usuário ou ajuste os filtros.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Nome & Identidade</th>
                  <th>E-mail</th>
                  <th>Roles RBAC</th>
                  <th>Verificado</th>
                  <th>Cadastrado Em</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-xs uppercase shrink-0 border border-blue-200">
                          {u.name ? u.name.substring(0, 2) : "US"}
                        </div>
                        <div>
                          <span className="font-semibold text-gray-900 block">{u.name}</span>
                          {u.username && (
                            <span className="text-xs text-muted block font-mono">
                              @{u.username}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="text-sm text-gray-800">{u.email}</span>
                    </td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {u.roles && u.roles.length > 0 ? (
                          u.roles.map((r) => (
                            <Badge key={r.id} variant="info" className="text-xs py-0">
                              {r.name}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-xs text-muted">Sem perfil</span>
                        )}
                      </div>
                    </td>
                    <td>
                      {u.is_verified ? (
                        <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                          <CheckCircle size={14} />
                          <span>Sim</span>
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs text-amber-600 font-medium">
                          <XCircle size={14} />
                          <span>Pendente</span>
                        </span>
                      )}
                    </td>
                    <td className="text-xs text-muted">{formatDate(u.created_at)}</td>
                    <td className="text-right">
                      <div className="flex justify-end gap-1">
                        <PermissionGuard permission="users:update">
                          <button
                            type="button"
                            className="btn-icon"
                            onClick={() => {
                              setUserToEdit(u);
                              setIsModalOpen(true);
                            }}
                            title="Editar usuário"
                          >
                            <Edit2 size={16} />
                          </button>
                        </PermissionGuard>

                        <PermissionGuard permission="users:delete">
                          <button
                            type="button"
                            className="btn-icon text-red-500 hover:text-red-700"
                            onClick={() => setUserToDelete(u)}
                            title="Excluir usuário"
                          >
                            <Trash2 size={16} />
                          </button>
                        </PermissionGuard>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* User Create/Edit Modal */}
      <UserModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={loadUsers}
        userToEdit={userToEdit}
      />

      {/* Delete User Modal */}
      {userToDelete && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal-overlay" onClick={() => setUserToDelete(null)} />
          <div className="modal-container modal-sm">
            <div className="modal-header">
              <h3 className="modal-title flex items-center gap-2 text-red-600">
                <ShieldAlert size={20} />
                <span>Confirmar Exclusão</span>
              </h3>
            </div>
            <div className="modal-body space-y-3">
              <p className="text-sm text-gray-700">
                Deseja realmente remover o usuário <strong>{userToDelete.name}</strong> (
                <code>{userToDelete.email}</code>)?
              </p>
              <div className="flex justify-end gap-3 pt-3">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setUserToDelete(null)}
                  disabled={isDeleting}
                >
                  Cancelar
                </Button>
                <Button variant="danger" size="sm" onClick={handleDelete} isLoading={isDeleting}>
                  Confirmar Exclusão
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
